import { dateTimeErrorMessage, zonedDate } from '../../../utils/date-time';
import moment from 'moment';
import 'moment/locale/pt-br';
import { AxiosError } from 'axios';
import { useAgendaContext } from './ContextAgenda';
import ScheduleItem from './ScheduleItem';
import ScheduleView from './view/ScheduleView';
import 'react-datepicker/dist/react-datepicker.css';
import { Modal } from '../../../components/Modal';
import { Drawer } from '../../../components/Drawer';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { Loading } from '../../../components/Loading';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ExtraActionsAgenda } from './list/ExtraActionsAgenda';
import { useMediaQuery } from 'react-responsive';
import { AgendaDaySurface } from './scheduler/AgendaDaySurface';
import { useAppContext } from '../../../contexts/AppContext';
import { HorariosAtendenteService } from '../Service';
import { useAppSnackbar } from '../../../components/useAppSnackbar';

moment.locale('pt-br');

const horariosAtendenteService = new HorariosAtendenteService();

const buildMovePayload = ({
    schedule,
    startAt,
    startMinutes,
    durationMinutes,
    localDate,
}: {
    schedule: any;
    startAt: Date;
    startMinutes: number;
    durationMinutes: number;
    localDate?: string;
}) => ({
    _id: schedule._id,
    observation: schedule.observation || '',
    observationClient: schedule.observationClient || '',
    startMinutes,
    localDate: localDate || moment(startAt).format('YYYY-MM-DD'),
    servicoId: schedule.service._id,
    servicosExtrasIds: (schedule.servicosExtras || []).map((extra: any) => extra._id),
    attendantId: schedule.attendant.uid,
    clientId: schedule.client.uid,
    duration: durationMinutes,
});

export default function ScheduleList({ agenda }: any) {
    const { clear } = useParamsContext();
    const { date, schedules, selectedSchedule, loading, selectedRange, setSelectedRange, settingsAgenda, reloadSchedules } =
        useAgendaContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { neutralSnackbar: openUndoSnackbar, closeSnackbar: closeUndoSnackbar } = useAppSnackbar();
    const isDesktop = useMediaQuery({ minWidth: 801 });

    const [displayedSchedule, setDisplayedSchedule] = useState<any>();
    const [isScheduleTransitionVisible, setIsScheduleTransitionVisible] = useState(false);
    const scheduleTransitionTimeoutRef = useRef<number | null>(null);
    const undoTokenRef = useRef(0);

    useEffect(() => {
        if (scheduleTransitionTimeoutRef.current) {
            window.clearTimeout(scheduleTransitionTimeoutRef.current);
            scheduleTransitionTimeoutRef.current = null;
        }

        if (!selectedSchedule) {
            setIsScheduleTransitionVisible(false);
            setDisplayedSchedule(undefined);
            return;
        }

        if (!displayedSchedule) {
            setDisplayedSchedule(selectedSchedule);
            window.requestAnimationFrame(() => setIsScheduleTransitionVisible(true));
            return;
        }

        if (displayedSchedule?._id === selectedSchedule?._id) {
            // Mesmo atendimento com dados novos (ex.: status apos concluir): atualiza sem transicao.
            if (displayedSchedule !== selectedSchedule) setDisplayedSchedule(selectedSchedule);
            return;
        }

        setIsScheduleTransitionVisible(false);
        scheduleTransitionTimeoutRef.current = window.setTimeout(() => {
            setDisplayedSchedule(selectedSchedule);
            window.requestAnimationFrame(() => setIsScheduleTransitionVisible(true));
            scheduleTransitionTimeoutRef.current = null;
        }, 150);
    }, [displayedSchedule, selectedSchedule]);

    useEffect(() => {
        return () => {
            if (scheduleTransitionTimeoutRef.current) {
                window.clearTimeout(scheduleTransitionTimeoutRef.current);
            }
        };
    }, []);

    const showMoveError = useCallback((
        error: unknown,
        fallbackMessage = 'Não foi possível mover o agendamento. Tente novamente em alguns instantes.'
    ) => {
        const message = error instanceof AxiosError ? error?.response?.data?.message : undefined;
        const timeError = dateTimeErrorMessage(message);
        if (timeError) { errorSnackbar(timeError); return; }

        if (message === 'conflictHours') {
            errorSnackbar('Este horário já está ocupado. Escolha outro horário disponível.');
        } else if (message === 'attendantServiceMismatch') {
            errorSnackbar('O atendente selecionado não atende um ou mais serviços escolhidos.');
        } else {
            errorSnackbar(fallbackMessage);
        }
    }, [errorSnackbar]);

    const handleAppointmentDrop = useCallback(async ({
        schedule,
        startAt,
        startMinutes,
        durationMinutes,
        localDate,
    }: {
        schedule: any;
        startAt: Date;
        startMinutes: number;
        durationMinutes: number;
        localDate?: string;
    }) => {
        const previousStartAt = new Date(schedule.startAt);
        const previousStartMinutes = schedule.startMinutes ?? (zonedDate(schedule.startAt, schedule.timezone).hour() * 60 + zonedDate(schedule.startAt, schedule.timezone).minute());

        try {
            await horariosAtendenteService.editAgendamento(buildMovePayload({
                schedule,
                localDate,
                startMinutes,
                startAt,
                durationMinutes,
            }));

            await reloadSchedules();
            closeUndoSnackbar();

            const undoToken = undoTokenRef.current + 1;
            undoTokenRef.current = undoToken;

            openUndoSnackbar(
                <div className="flex w-full items-center justify-between gap-4">
                    <span className="min-w-0">Agendamento movido.</span>
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs ml-auto h-auto min-h-0 shrink-0 px-2 py-1 text-primary hover:bg-primary/10"
                        onClick={async (event) => {
                            event.preventDefault();
                            event.stopPropagation();

                            if (undoTokenRef.current !== undoToken) {
                                return;
                            }

                            closeUndoSnackbar();

                            try {
                                await horariosAtendenteService.editAgendamento({ ...buildMovePayload({
                                    schedule,
                                    startAt: previousStartAt,
                                    startMinutes: previousStartMinutes,
                                    durationMinutes,
                                }), localDate: undefined, startAt: previousStartAt });
                                await reloadSchedules();
                                successSnackbar('Movimentação desfeita.');
                            } catch (error) {
                                showMoveError(
                                    error,
                                    'Não foi possível desfazer a movimentação. Tente novamente em alguns instantes.'
                                );
                            }
                        }}
                    >
                        Desfazer
                    </button>
                </div>,
                10000
            );
        } catch (error) {
            showMoveError(error);

            throw error;
        }
    }, [
        closeUndoSnackbar,
        openUndoSnackbar,
        reloadSchedules,
        showMoveError,
        successSnackbar,
    ]);

    const handleInvalidAppointmentDrop = useCallback(({
        reason,
    }: {
        schedule: any;
        reason: 'conflict';
    }) => {
        errorSnackbar('Este horário já está ocupado. Escolha outro horário disponível.');
    }, [errorSnackbar]);

    if (loading) {
        return (
            <div className="w-full h-full items-center justify-center flex">
                <div className="text-center">
                    <Loading />
                </div>
            </div>
        );
    }

    return (
        <div className={`relative ${selectedRange ? 'pb-40 sm:pb-0' : ''}`}>
            <AgendaDaySurface
                schedules={schedules}
                date={date}
                settingsAgenda={settingsAgenda}
                selectedRange={selectedRange}
                setSelectedRange={setSelectedRange}
                onAppointmentDrop={handleAppointmentDrop}
                onInvalidAppointmentDrop={handleInvalidAppointmentDrop}
                renderAppointment={({ schedule, layout }) => (
                    <ScheduleItem schedule={schedule} layout={layout} />
                )}
            />

            {selectedRange && (
                <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+0.5rem)] left-1/2 z-50 w-[calc(100vw-1rem)] max-w-[24rem] -translate-x-1/2 sm:bottom-5 sm:left-auto sm:right-5 sm:w-auto sm:max-w-none sm:translate-x-0">
                    <ExtraActionsAgenda agenda={agenda} />
                </div>
            )}

            {!!selectedSchedule && (
                isDesktop ? (
                    <Drawer
                        onClose={clear}
                        title="Agendamento"
                        widthClass="w-full max-w-2xl"
                        backdropStyle={{ background: 'rgb(0 0 0 / 0.18)', backdropFilter: 'blur(4px)' }}
                        panelClassName="sf-schedule-detail-panel"
                        headerClassName="h-[68px] px-5 py-0 items-center"
                    >
                        <div
                            className={`transition-[opacity,transform] duration-200 ease-out ${
                                isScheduleTransitionVisible
                                    ? 'translate-x-0 opacity-100'
                                    : 'translate-x-2 opacity-0'
                            }`}
                        >
                            {!!displayedSchedule && <ScheduleView schedule={displayedSchedule} />}
                        </div>
                    </Drawer>
                ) : (
                    <Modal onClose={clear} title="Agendamento" panelClassName="sf-schedule-detail-panel">
                        <div
                            className={`transition-[opacity,transform] duration-200 ease-out ${
                                isScheduleTransitionVisible
                                    ? 'translate-x-0 opacity-100'
                                    : 'translate-x-2 opacity-0'
                            }`}
                        >
                            {!!displayedSchedule && <ScheduleView schedule={displayedSchedule} />}
                        </div>
                    </Modal>
                )
            )}
        </div>
    );
}
